/*
 * Obsidian Workwear: automatic logo setup fee.
 * Adds the "Logo setup fee" (one per order) when the basket is under £200
 * and contains no packs (packs include setup). Removes it otherwise.
 * Customers tagged "logo-on-file" never pay it while logged in
 * (window.ObsidianFee is set in snippets/skip-to-content-link.liquid).
 *
 * Also keeps print add-ons in step with the garments: every cart line with the
 * property "Sleeve print", "Back of neck print" or "Large front print" set to Yes needs one matching
 * add-on per garment, so the add-on line quantities always equal those totals.
 *
 * 2026-10 rewrite: this script no longer intercepts the theme's cart requests (window.fetch)
 * and no longer fires cart events into the theme, both of which stopped customers changing
 * quantities in the cart. It now only LISTENS for cart changes, waits for the theme to finish,
 * then makes its own changes and refreshes the cart (full reload on /cart, section re-render
 * in the drawer).
 */
import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const FEE_VARIANT = 55518440489289;
const FREE_FROM = 20000; // pence, inc VAT
const PACK_TYPE = 'Packs';
const ADDONS = [
  { prop: 'Sleeve print', variant: 55518850974025 },
  { prop: 'Back of neck print', variant: 55518851006793 },
  { prop: 'Large front print', variant: 55518864539977 },
];
const ADDON_IDS = new Set(ADDONS.map((a) => a.variant));
const isExtra = (i) => i.variant_id === FEE_VARIANT || ADDON_IDS.has(i.variant_id);

let running = null;
let again = false;
let leavingForCheckout = false;

const onCartPage = () => /^\/cart\/?$/.test(window.location.pathname);

// Grey out the quantity controls on the fee and add-on lines (this script sets those itself).
(function lockExtraLines() {
  const ids = [FEE_VARIANT, ...ADDON_IDS];
  const sel = ids.map((id) => `[data-variant-id="${id}"] button, [data-variant-id="${id}"] input`).join(',\n');
  const style = document.createElement('style');
  style.textContent = `${sel} { pointer-events: none !important; opacity: 0.5; }`;
  document.head.appendChild(style);
})();

// Wait until the theme has finished any cart quantity update it is part-way through.
async function waitForTheme() {
  for (let i = 0; i < 20; i++) {
    const CartItems = customElements.get('cart-items-component');
    const pending = (CartItems && CartItems.pendingQuantityUpdates) || [];
    if (!pending.length) return;
    await Promise.allSettled(pending);
  }
}

const cartJson = () =>
  fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());

const post = (url, body) =>
  fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json());

const state = () => window.ObsidianFee || {};
let feeInCart = false;
let hasItems = false;

function feeNeeded(cart) {
  if (state().logoOnFile) return false; // returning customer, logo already on file
  const garments = cart.items.filter((i) => !isExtra(i));
  if (!garments.length) return false;
  if (garments.some((i) => i.product_type === PACK_TYPE)) return false;
  const subtotal = cart.items
    .filter((i) => i.variant_id !== FEE_VARIANT)
    .reduce((sum, i) => sum + i.final_line_price, 0);
  return subtotal < FREE_FROM;
}

async function refreshCartUI() {
  if (leavingForCheckout) return;
  // On the cart page a full reload guarantees the line positions are correct.
  if (onCartPage()) {
    window.location.reload();
    return;
  }
  // Elsewhere (cart drawer): re-render each cart section with the theme's own renderer.
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    try {
      await sectionRenderer.renderSection(el.dataset.sectionId, {
        cache: false,
        mode: el.dataset.drawer !== undefined ? 'hydration' : 'full',
      });
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[setup-fee] refresh failed', error);
    }
  }
}

const qtyOf = (cart, variant) =>
  cart.items.filter((i) => i.variant_id === variant).reduce((s, i) => s + i.quantity, 0);

async function syncAddons(cart) {
  const toAdd = [];
  const updates = {};
  for (const a of ADDONS) {
    const want = cart.items
      .filter((i) => !isExtra(i) && i.properties && i.properties[a.prop] === 'Yes')
      .reduce((s, i) => s + i.quantity, 0);
    const have = qtyOf(cart, a.variant);
    if (want === have) continue;
    if (have === 0) toAdd.push({ id: a.variant, quantity: want });
    else updates[a.variant] = want;
  }
  if (toAdd.length) await post('/cart/add.js', { items: toAdd });
  if (Object.keys(updates).length) await post('/cart/update.js', { updates });
  return toAdd.length > 0 || Object.keys(updates).length > 0;
}

async function sync() {
  await waitForTheme();
  let cart = await cartJson();
  const addonsChanged = await syncAddons(cart);
  if (addonsChanged) cart = await cartJson();

  const need = feeNeeded(cart);
  const qty = qtyOf(cart, FEE_VARIANT);
  feeInCart = need;
  hasItems = cart.items.some((i) => !isExtra(i));
  renderNotes();

  let feeChanged = true;
  if (need && qty === 0) await post('/cart/add.js', { items: [{ id: FEE_VARIANT, quantity: 1 }] });
  else if (need && qty !== 1) await post('/cart/update.js', { updates: { [FEE_VARIANT]: 1 } });
  else if (!need && qty > 0) await post('/cart/update.js', { updates: { [FEE_VARIANT]: 0 } });
  else feeChanged = false;

  if (!addonsChanged && !feeChanged) return false;
  await waitForTheme();
  await refreshCartUI();
  return true;
}

// Basket messages next to every checkout button (cart page and drawer).
function noteHtml() {
  const s = state();
  if (!hasItems) return '';
  if (s.logoOnFile) return "Your logo's on file, so there's no setup fee on this order.";
  if (feeInCart && !s.loggedIn) {
    const base = s.loginUrl || '/account/login';
    const url = `${base}${base.includes('?') ? '&' : '?'}return_to=${encodeURIComponent('/cart')}`;
    return `Ordered with us before? <a href="${url}">Log in</a> and the setup fee comes off.`;
  }
  return '';
}

function renderNotes() {
  const html = noteHtml();
  for (const button of document.querySelectorAll('button[name="checkout"], #checkout')) {
    const anchor = button.closest('form') || button.parentElement;
    if (!anchor || !anchor.parentElement) continue;
    let note = anchor.previousElementSibling;
    const isNote = note && note.classList.contains('obsidian-fee-note');
    if (!html) {
      if (isNote) note.remove();
      continue;
    }
    if (!isNote) {
      note = document.createElement('p');
      note.className = 'obsidian-fee-note';
      anchor.parentElement.insertBefore(note, anchor);
    }
    // Compare against what we last wrote, so this never rewrites itself in a loop.
    if (note.dataset.html !== html) {
      note.dataset.html = html;
      note.innerHTML = html;
    }
  }
}

let notesQueued = false;
new MutationObserver(() => {
  if (notesQueued) return;
  notesQueued = true;
  setTimeout(() => {
    notesQueued = false;
    renderNotes();
  }, 100);
}).observe(document.body, { childList: true, subtree: true });

function schedule() {
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    try {
      do {
        again = false;
        await sync();
      } while (again);
    } catch (error) {
      console.warn('[setup-fee]', error);
    } finally {
      running = null;
    }
  })();
  return running;
}

// Listen (only listen) for cart changes made by the theme, then re-check once they finish.
for (const name of Object.values(StandardEvents || {})) {
  if (typeof name !== 'string' || !/cart/i.test(name)) continue;
  document.addEventListener(name, (event) => {
    Promise.resolve(event.promise)
      .catch(() => {})
      .then(() => setTimeout(schedule, 300));
  });
}

// Make sure the basket is right before checkout.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    const submitter = event.submitter;
    const goingToCheckout =
      (submitter && submitter.name === 'checkout') || (form && /\/checkout/.test(form.getAttribute('action') || ''));
    if (!goingToCheckout) return;
    event.preventDefault();
    leavingForCheckout = true;
    schedule().finally(() => {
      window.location.href = '/checkout';
    });
  },
  true
);

window.addEventListener('pageshow', () => schedule());
schedule();
